import React, { useState } from 'react';
import { Bell, Tag, Mail, MessageSquare, X, Send } from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

import ReactGA from 'react-ga4';
import { supabase } from '../../services/supabase';
import { getAggregatedTransactions } from './services/dataService';
import categoryData from './data/categoryData';

const trackEvent = (action, label) => {
  ReactGA.event({ category: 'PropertyPro', action, label });
};

const USE_LOCAL_DATA = process.env.REACT_APP_USE_LOCAL_DATA !== 'false';

const PropertyPro = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [annotations] = useState([
    { user: 'Sarah Chen', note: 'This trend needs review - occupancy dropping', time: '2h ago' }
  ]);

  const dashboardData = [
    {
      id: 1,
      title: 'Pre-Development & Market Insight',
      items: [
        { name: 'Market Demographics & Economics', sync: '1m ago' },
        { name: 'Zoning & Land Use', sync: '1hr ago' },
        { name: 'Property Transactions', sync: '5m ago' },
        { name: 'Environmental / Site', sync: '20hr ago' }
      ]
    },
    {
      id: 2,
      title: 'Design & Planning',
      items: [
        { name: 'Architecture & BIM', sync: '1m ago' },
        { name: 'Site / Massing Optimization', sync: '1hr ago' },
        { name: 'Cost Estimation', sync: '5m ago' },
        { name: 'Sustainability / Energy', sync: '20hr ago' }
      ]
    },
    {
      id: 3,
      title: 'Construction & Execution',
      items: [
        { name: 'Project Management', sync: '1m ago' },
        { name: 'Scheduling', sync: '1hr ago' },
        { name: 'Progress Capture', sync: '5m ago' },
        { name: 'Procurement / Supply Chain', sync: '20hr ago' },
        { name: 'Safety & Compliance', sync: '20hr ago' }
      ]
    },
    {
      id: 4,
      title: 'Leasing Operations & Activation',
      items: [
        { name: 'Retail Sales & Foot Traffic', sync: '1m ago' },
        { name: 'Tenant / Leasing Data', sync: '1hr ago' },
        { name: 'Access & Movement Analytics', sync: '5m ago' },
        { name: 'Energy & Building Systems', sync: '20hr ago' }
      ]
    },
    {
      id: 5,
      title: 'Long-Term Asset Optimization',
      items: [
        { name: 'Asset Performance & Financial', sync: '1m ago' },
        { name: 'Maintenance & Lifecycle', sync: '1hr ago' },
        { name: 'ESG & Sustainability', sync: '5m ago' },
        { name: 'Benchmarking & Market Research', sync: '20hr ago' }
      ]
    }
  ];

  const ActionButton = ({ icon: Icon, label, onClick }) => (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 rounded-md transition-colors w-full text-left"
    >
      <Icon className="w-4 h-4 text-purple-600" />
      <span>{label}</span>
    </button>
  );

  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-xl w-full max-w-md m-4"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4">{children}</div>
        </div>
      </div>
    );
  };

  const ReminderModal = () => (
    <Modal
      isOpen={showReminderModal}
      onClose={() => setShowReminderModal(false)}
      title="Set Reminder"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            rows={3}
            placeholder="Review market demographics data..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Remind me on</label>
          <input
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
          <input
            type="time"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <button className="w-full bg-purple-600 text-white py-2 rounded-md hover:bg-purple-700 flex items-center justify-center gap-2">
          <Bell className="w-4 h-4" />
          Set Reminder
        </button>
      </div>
    </Modal>
  );

  const TagModal = () => (
    <Modal
      isOpen={showTagModal}
      onClose={() => setShowTagModal(false)}
      title="Tag Team Member"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select person to tag
          </label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
            <option>John Smith (Developer)</option>
            <option>Sarah Chen (Architect)</option>
            <option>Mike Johnson (Project Manager)</option>
            <option>Lisa Wong (Analyst)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Highlight on</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
            <option>Current Chart</option>
            <option>Specific Metric</option>
            <option>Data Point</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Add note</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            rows={3}
            placeholder="Check this trend - looks unusual..."
          />
        </div>
        <button className="w-full bg-purple-600 text-white py-2 rounded-md hover:bg-purple-700 flex items-center justify-center gap-2">
          <Tag className="w-4 h-4" />
          Tag & Notify
        </button>
      </div>
    </Modal>
  );

  const ReportModal = () => (
    <Modal
      isOpen={showReportModal}
      onClose={() => setShowReportModal(false)}
      title="Send Report"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Recipients</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder="Enter email addresses..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
            <option>Current Dashboard</option>
            <option>Summary Report</option>
            <option>Detailed Analysis</option>
            <option>Custom Selection</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="format" defaultChecked />
              PDF
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="format" />
              Excel
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="format" />
              PowerPoint
            </label>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
            <option>Send Now</option>
            <option>Daily</option>
            <option>Weekly</option>
            <option>Monthly</option>
          </select>
        </div>
        <button className="w-full bg-purple-600 text-white py-2 rounded-md hover:bg-purple-700 flex items-center justify-center gap-2">
          <Send className="w-4 h-4" />
          Send Report
        </button>
      </div>
    </Modal>
  );

  const MainView = () => (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {dashboardData.map(card => (
        <div
          key={card.id}
          className="flex-shrink-0 w-48 bg-purple-50 rounded-xl border border-purple-100 cursor-pointer hover:shadow-lg transition-shadow relative overflow-hidden"
          onClick={() => {
            trackEvent('select_card', card.title);
            setSelectedCard(card.id);
            setSelectedItem(null);
          }}
        >
          <div className="absolute left-0 top-0 bottom-0 w-6 bg-purple-600 flex items-center justify-center">
            <span className="text-white text-xs font-medium transform -rotate-90 whitespace-nowrap">
              {card.title.split(' ').slice(0, 2).join(' ')}
            </span>
          </div>

          <div className="ml-6 p-3">
            <div className="w-7 h-7 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm mb-2">
              {card.id}
            </div>

            <h3 className="font-bold text-gray-800 text-sm mb-3 leading-tight">{card.title}</h3>

            <div className="space-y-2">
              {card.items.map((item, idx) => (
                <div key={idx} className="bg-white rounded-md p-2 shadow-sm">
                  <p className="text-xs font-medium text-gray-700 leading-tight">{item.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Synced {item.sync}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const PropertyTransactionsDashboard = ({ data, loading }) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full text-sm text-gray-500">
          Loading property transactions...
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-sm text-gray-500">
          No property transactions found for this project.
        </div>
      );
    }

    // Chart A: Avg $PSF by Street
    const chartAData = data.map(row => ({
      street_name: row.street_name,
      avg_unit_price_psf: Number(row.avg_unit_price_psf || 0),
    }));

    // Chart B: Total transacted price by Project
    const totalsByProject = data.reduce((acc, row) => {
      const key = row.project_name || 'Unknown';
      acc[key] = (acc[key] || 0) + Number(row.total_transacted_price || 0);
      return acc;
    }, {});
    const chartBData = Object.entries(totalsByProject).map(([project_name, total]) => ({
      project_name,
      total_transacted_price: total,
    }));

    // Chart C: $PSF over time (using last_sale_date)
    const chartCData = data
      .map(row => ({
        sale_date: row.last_sale_date,
        avg_unit_price_psf: Number(row.avg_unit_price_psf || 0),
      }))
      .sort((a, b) => (a.sale_date || '').localeCompare(b.sale_date || ''));

    // Chart D: Distribution by Property Type (share of total value)
    const totalsByType = data.reduce((acc, row) => {
      const key = row.property_type || 'Unknown';
      acc[key] = (acc[key] || 0) + Number(row.total_transacted_price || 0);
      return acc;
    }, {});
    const chartDData = Object.entries(totalsByType).map(([property_type, total]) => ({
      property_type,
      total,
    }));

    const pieColors = ['#7C3AED', '#EC4899', '#06B6D4', '#22C55E', '#F97316'];

    const formatCurrency = (value) => {
      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
      return `$${value}`;
    };

    const formatTooltipValue = (value) =>
      `$${Number(value).toLocaleString('en-SG', { maximumFractionDigits: 0 })}`;

    const truncateLabel = (label, maxLen = 14) =>
      label && label.length > maxLen ? label.slice(0, maxLen) + '...' : label;

    const pieTotalValue = chartDData.reduce((sum, d) => sum + d.total, 0);

    const renderPieLabel = ({ property_type, total, cx, cy, midAngle, outerRadius }) => {
      const RADIAN = Math.PI / 180;
      const radius = outerRadius + 20;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      const pct = ((total / pieTotalValue) * 100).toFixed(1);
      return (
        <text x={x} y={y} fill="#374151" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11}>
          {pct}%
        </text>
      );
    };

    return (
      <div className="flex flex-col gap-6">
        {/* Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-800">Property Transactions (Aggregated)</h4>
            <span className="text-xs text-gray-400">{data.length} rows</span>
          </div>
          <div className="max-h-64 overflow-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Project</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Street</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600"># Txns</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Total $</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Avg $ PSF</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">First Sale</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Last Sale</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-2 whitespace-nowrap">{row.project_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.street_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{row.property_type}</td>
                    <td className="px-3 py-2 text-right">{row.num_transactions}</td>
                    <td className="px-3 py-2 text-right">
                      {Number(row.total_transacted_price || 0).toLocaleString('en-SG', {
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {Number(row.avg_unit_price_psf || 0).toLocaleString('en-SG', {
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                      {row.first_sale_date}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                      {row.last_sale_date}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Charts grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* A: Avg $ PSF by Street */}
          <div className="border border-gray-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Avg $ PSF by Street</h4>
            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartAData} margin={{ top: 10, right: 10, left: 5, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="street_name"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    tick={{ fontSize: 10 }}
                    height={70}
                    tickFormatter={(v) => truncateLabel(v)}
                  />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                  <Tooltip formatter={(v) => [formatTooltipValue(v), 'Avg $ PSF']} />
                  <Bar dataKey="avg_unit_price_psf" name="Avg $ PSF" fill="#7C3AED" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* B: Total transacted price by Project */}
          <div className="border border-gray-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Total Transacted Value by Project</h4>
            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartBData} margin={{ top: 10, right: 10, left: 5, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="project_name"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    tick={{ fontSize: 10 }}
                    height={70}
                    tickFormatter={(v) => truncateLabel(v, 18)}
                  />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={formatCurrency} />
                  <Tooltip formatter={(v) => [formatTooltipValue(v), 'Total Value']} />
                  <Bar dataKey="total_transacted_price" name="Total $" fill="#22C55E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* C: PSF over time */}
          <div className="border border-gray-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Avg $ PSF Over Time</h4>
            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartCData} margin={{ top: 10, right: 10, left: 5, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="sale_date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                  <Tooltip formatter={(v) => [formatTooltipValue(v), 'Avg $ PSF']} />
                  <Line
                    type="monotone"
                    dataKey="avg_unit_price_psf"
                    name="Avg $ PSF"
                    stroke="#F97316"
                    strokeWidth={2}
                    dot={{ r: 4, fill: '#F97316' }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* D: Property type distribution */}
          <div className="border border-gray-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Value Share by Property Type</h4>
            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip formatter={(v, name) => [formatTooltipValue(v), name]} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 11 }}
                  />
                  <Pie
                    data={chartDData}
                    dataKey="total"
                    nameKey="property_type"
                    cx="50%"
                    cy="45%"
                    outerRadius={75}
                    label={renderPieLabel}
                    labelLine={{ stroke: '#9CA3AF', strokeWidth: 1 }}
                  >
                    {chartDData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const pieColors = ['#7C3AED', '#EC4899', '#06B6D4', '#22C55E', '#F97316'];

  const toneClass = {
    up: 'text-emerald-600 bg-emerald-50',
    down: 'text-red-600 bg-red-50',
    flat: 'text-gray-500 bg-gray-100',
  };

  // Generic KPI + chart + insight renderer, driven by categoryData — used for every
  // sidebar item other than "Property Transactions".
  const CategoryDashboard = ({ config }) => {
    const { kpis, chart, insight } = config;

    return (
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500">{kpi.label}</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-lg font-semibold text-gray-800">{kpi.value}</p>
                {kpi.delta && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${toneClass[kpi.tone] || toneClass.flat}`}>
                    {kpi.delta}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="border border-gray-200 rounded-lg p-3">
          <h4 className="text-xs font-semibold text-gray-700 mb-2">{chart.title}</h4>
          <div className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              {chart.type === 'pie' ? (
                <PieChart>
                  <Tooltip />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Pie
                    data={chart.data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    outerRadius={75}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {chart.data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                </PieChart>
              ) : chart.type === 'line' ? (
                <LineChart data={chart.data} margin={{ top: 10, right: 10, left: 5, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={chart.xKey} angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={50} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  {chart.series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                  {chart.series.map((s) => (
                    <Line
                      key={s.dataKey}
                      type="monotone"
                      dataKey={s.dataKey}
                      name={s.name}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={{ r: 3, fill: s.color }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              ) : (
                <BarChart data={chart.data} margin={{ top: 10, right: 10, left: 5, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={chart.xKey} angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={50} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  {chart.series.map((s) => (
                    <Bar key={s.dataKey} dataKey={s.dataKey} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-purple-600 mb-1.5 uppercase tracking-wide">Insight</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{insight}</p>
        </div>
      </div>
    );
  };

  const DetailView = () => {
    const [propertyData, setPropertyData] = useState([]);
    const [loadingProperty, setLoadingProperty] = useState(false);

    const demoProjectId = process.env.REACT_APP_DEMO_PROJECT_ID;

    const card = dashboardData.find(c => c.id === selectedCard);
    const currentItem = card && selectedItem !== null ? card.items[selectedItem] : null;

    React.useEffect(() => {
      if (!card || !currentItem) return;
      if (currentItem.name !== 'Property Transactions') return;

      setLoadingProperty(true);

      if (USE_LOCAL_DATA) {
        getAggregatedTransactions(demoProjectId)
          .then(({ data, error }) => {
            if (error) {
              console.error('Error fetching property transactions', error);
              setPropertyData([]);
            } else {
              setPropertyData(data || []);
            }
          })
          .finally(() => setLoadingProperty(false));
      } else {
        if (!demoProjectId || !supabase) {
          setLoadingProperty(false);
          return;
        }
        supabase
          .from('vw_project_property_transactions')
          .select('*')
          .eq('project_id', demoProjectId)
          .then(({ data, error }) => {
            if (error) {
              console.error('Error fetching property transactions', error);
              setPropertyData([]);
            } else {
              setPropertyData(data || []);
            }
          })
          .finally(() => setLoadingProperty(false));
      }
    }, [card, currentItem, demoProjectId]);

    if (!card) return null;

    return (
      <div className="flex h-full">
        <div className="w-64 flex-shrink-0">
          <div className="bg-purple-600 rounded-t-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => {
                  if (selectedItem !== null) {
                    setSelectedItem(null);
                  } else {
                    setSelectedCard(null);
                  }
                }}
                className="text-white hover:text-purple-200"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white font-bold text-xs">
                {card.id}
              </div>
            </div>
            <h2 className="text-white font-bold text-sm leading-tight">{card.title}</h2>
          </div>

          <div className="bg-purple-50 rounded-b-xl border border-purple-100 border-t-0">
            {dashboardData.map(navCard => (
              <div
                key={navCard.id}
                className={`p-3 cursor-pointer border-b border-purple-100 last:border-b-0 ${
                  navCard.id === selectedCard ? 'bg-purple-100' : 'hover:bg-purple-100'
                }`}
                onClick={() => {
                  setSelectedCard(navCard.id);
                  setSelectedItem(null);
                }}
              >
                <div className="flex items-start gap-2">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${
                      navCard.id === selectedCard
                        ? 'bg-purple-600 text-white'
                        : 'bg-purple-200 text-purple-600'
                    }`}
                  >
                    {navCard.id}
                  </div>
                  {navCard.id === selectedCard ? (
                    <div className="flex-1">
                      {navCard.items.map((item, idx) => (
                        <div
                          key={idx}
                          className={`py-1.5 first:pt-0 cursor-pointer rounded px-1 -mx-1 ${
                            selectedItem === idx ? 'bg-purple-200' : 'hover:bg-purple-200'
                          }`}
                          onClick={e => {
                            e.stopPropagation();
                            trackEvent('select_item', item.name);
                            setSelectedItem(idx);
                          }}
                        >
                          <p className="text-xs font-medium text-gray-700">{item.name}</p>
                          <p className="text-xs text-gray-400">Synced {item.sync}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs font-medium text-gray-700 leading-tight">
                      {navCard.title}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 ml-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <div className="h-1 bg-gray-200 rounded-full">
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-purple-600 rounded-full border-2 border-white shadow" />
                  </div>
                </div>
              </div>
              <button className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800">
                Modify
              </button>
              <button className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800">
                Filter
              </button>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className="px-4 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 flex items-center gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  Actions
                </button>

                {showActionsMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                    <ActionButton
                      icon={Bell}
                      label="Set Reminder"
                      onClick={() => {
                        trackEvent('open_modal', 'Set Reminder');
                        setShowReminderModal(true);
                        setShowActionsMenu(false);
                      }}
                    />
                    <ActionButton
                      icon={Tag}
                      label="Tag Team Member"
                      onClick={() => {
                        trackEvent('open_modal', 'Tag Team Member');
                        setShowTagModal(true);
                        setShowActionsMenu(false);
                      }}
                    />
                    <ActionButton
                      icon={Mail}
                      label="Send Report"
                      onClick={() => {
                        trackEvent('open_modal', 'Send Report');
                        setShowReportModal(true);
                        setShowActionsMenu(false);
                      }}
                    />
                  </div>
                )}
              </div>
              <button
                onClick={() => trackEvent('click_download', 'Download')}
                className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-full hover:bg-purple-700"
              >
                Download
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 min-h-[400px] max-h-[700px] overflow-y-auto p-4">
            {currentItem ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">{currentItem.name}</h3>
                  <span className="text-xs text-gray-400">Synced {currentItem.sync}</span>
                </div>

                {currentItem.name === 'Property Transactions' ? (
                  <PropertyTransactionsDashboard
                    data={propertyData}
                    loading={loadingProperty}
                  />
                ) : (
                  <>
                    {selectedItem === 0 && annotations.length > 0 && (
                      <div className="mb-4 space-y-2">
                        {annotations.map((annotation, idx) => (
                          <div
                            key={idx}
                            className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-xs rounded"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Tag className="w-3 h-3 text-yellow-600" />
                                  <span className="font-medium text-yellow-800">
                                    @{annotation.user}
                                  </span>
                                </div>
                                <p className="text-gray-700">{annotation.note}</p>
                              </div>
                              <span className="text-yellow-600 text-xs whitespace-nowrap ml-2">
                                {annotation.time}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {categoryData[currentItem.name] ? (
                      <CategoryDashboard config={categoryData[currentItem.name]} />
                    ) : (
                      <div className="h-48 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-12 h-12 bg-white rounded-lg mx-auto mb-3 flex items-center justify-center shadow-sm">
                            <svg
                              className="w-6 h-6 text-purple-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                              />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-gray-700">{currentItem.name}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Charts, metrics &amp; data visualization
                          </p>
                          <p className="text-xs text-purple-600 mt-3 font-medium">
                            Click "Actions" to collaborate
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-400">Select a data category</p>
                  <p className="text-xs text-gray-300 mt-1">
                    Click on any item in the sidebar
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-600 rounded-lg" />
              <span className="font-semibold text-gray-800">PropertyData Pro</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-48 h-8 bg-gray-100 rounded-lg px-3 flex items-center">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <span className="text-sm text-gray-400 ml-2">Search...</span>
              </div>
              <div className="relative">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center cursor-pointer hover:bg-purple-200">
                  <Bell className="w-4 h-4 text-purple-600" />
                </div>
                <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />
              </div>
              <div className="w-8 h-8 bg-purple-200 rounded-full" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {selectedCard ? <DetailView /> : <MainView />}
        </div>

        <p className="text-center text-sm text-gray-400 mt-4">
          {selectedCard
            ? selectedItem !== null
              ? 'Use Actions to set reminders, tag team members, or send reports'
              : 'Click on a sub-item to view its dashboard'
            : 'Click on any card to view the expanded dashboard'}
        </p>
      </div>

      <ReminderModal />
      <TagModal />
      <ReportModal />
    </div>
  );
};

export default PropertyPro;
